/**
 * The call-note templates the board offers — Figma 6089:3673, in its order.
 *
 * `label` is what the chip reads; `message` is what picking it writes into
 * "Message to patient". The copy is product's, word for word (2026-09-12), and
 * lives HERE and nowhere else, so what a patient receives can be reviewed in
 * one place — the modal only renders it.
 *
 * TWO MESSAGES ARE SHARED, as product wrote them: "Not answered" and "Not
 * answered · 3 attempts" send the same text, and so do "Physiotherapist call"
 * and "Psychologist call". They stay separate chips because the board draws
 * them separately and they follow different dispositions.
 *
 * NO SALUTATION TITLE. These read "Dear {patientName}," — the "Mr/Ms" product
 * originally wrote was dropped on 2026-09-28 (Nittin). It was never a token:
 * the patient's gender is not consulted here, so it printed for everyone, and
 * "Dear Mr/Ms Rittika TestUser" reads worse than the bare name. If a title is
 * ever wanted it has to come from the patient's gender, not from the copy.
 */
const MESSAGES = {
  /* Navigo status marking (the CALL NOT CONNECTED group). */
  notReachable:
    "Dear {patientName}, {providerName} tried reaching you for the scheduled call at {callTime} {callDate} but unfortunately your number was unreachable. Please let us know if you would like to reschedule this call.",
  notAnswered:
    "Dear {patientName}, {providerName} tried reaching you for the scheduled call at {callTime} {callDate} but unfortunately it was not answered. Please let us know if you would like to reschedule this call.",
  callLater:
    "Dear {patientName}, {providerName} tried reaching you for the scheduled call at {callTime} {callDate} but we understand you requested a reschedule. Please let us know for when you would like to reschedule this call.",
  /* Call notes (the CALL COMPLETED group). */
  successfulPrc:
    "Dear {patientName}, hope the call went well with {providerName} and all your health queries were covered during the call. We have put together guidelines suggested by your care team during the call, please follow as suggested and do let us know in case of any queries",
  successfulHra:
    "Dear {patientName}, hope the call went well with {providerName} and all your health issues were covered during the call. You will receive the health plan within 24 hours. Do let us know in case of any queries.",
  reportsAnalysis:
    "Dear {patientName}, your nutritionist {providerName} has shared below guidelines as per your latest reports. Please follow them along with the health plan. Do let us know in case of any queries",
  doctorCall:
    "Dear {patientName}, hope the call went well with {providerName} and all your health issues were covered during the call. Please follow the prescription as advised.",
  therapistCall:
    "Dear {patientName}, hope the call went well with {providerName} and all your health issues were covered during the call. We have put together guidelines suggested by your care team during the call, please follow as suggested and do let us know in case of any queries",
};

export const CALL_NOTE_TEMPLATE_GROUPS = [
  {
    key: "notConnected",
    label: "CALL NOT CONNECTED",
    templates: [
      { key: "notReachable", label: "Not reachable", message: MESSAGES.notReachable },
      { key: "notAnswered", label: "Not answered", message: MESSAGES.notAnswered },
      { key: "notAnswered3", label: "Not answered · 3 attempts", message: MESSAGES.notAnswered },
      { key: "callLater", label: "Call later", message: MESSAGES.callLater },
    ],
  },
  {
    key: "completed",
    label: "CALL COMPLETED",
    templates: [
      { key: "successfulPrc", label: "Successful PRC", message: MESSAGES.successfulPrc },
      { key: "successfulHra", label: "Successful HRA", message: MESSAGES.successfulHra },
      { key: "reportsAnalysis", label: "Reports analysis", message: MESSAGES.reportsAnalysis },
      { key: "doctorCall", label: "Doctor call", message: MESSAGES.doctorCall },
      { key: "physiotherapistCall", label: "Physiotherapist call", message: MESSAGES.therapistCall },
      { key: "psychologistCall", label: "Psychologist call", message: MESSAGES.therapistCall },
    ],
  },
];

/** Every template, flat — for lookups and for tests that walk the whole set. */
export const CALL_NOTE_TEMPLATES = CALL_NOTE_TEMPLATE_GROUPS.reduce(
  (all, group) => all.concat(group.templates),
  []
);

export const findCallNoteTemplate = (key) =>
  CALL_NOTE_TEMPLATES.filter((t) => t.key === key)[0] || null;

/**
 * "10:30 AM" from minutes-since-midnight — formatTimeSlot's no-duration arm,
 * inlined ON PURPOSE: this file is carried verbatim by bridge's fork of the
 * modal, and bridge has no modules/appointments/helper/formatters.
 */
const slotTime = (minutes) => {
  if (minutes === undefined || minutes === null || isNaN(Number(minutes))) return "";
  const m = Number(minutes);
  const h24 = Math.floor(m / 60) % 24;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m % 60).padStart(2, "0")} ${h24 < 12 ? "AM" : "PM"}`;
};

/**
 * "20/08/26" — the copy's DD/MM/YY. UTC for the same reason formatDate is:
 * `appointmentDate` is a UTC instant, and rendering it in the viewer's zone
 * slides it across a day boundary. (formatDate itself is "20 Aug 2026", which
 * is not the shape product wrote.)
 */
const shortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${p(d.getUTCFullYear() % 100)}`;
};

/**
 * What a template's tokens stand for, from the data this screen already holds.
 *
 * A value we cannot resolve is left as PRODUCT'S OWN placeholder — "<user
 * name>", "--:--" — rather than silently dropped or invented. The operator
 * edits the message before sending, so a visible gap is a prompt; an empty one
 * would go out reading "hope the call went well with ."
 */
export const callNoteTemplateContext = ({ patient, appointment }) => {
  const fromPatient = patient
    ? `${patient.firstName || ""} ${patient.lastName || ""}`.trim()
    : "";
  const name = fromPatient || (appointment && appointment.patientName) || "";
  const slot = appointment && appointment.timeSlot;
  return {
    patientName: name || "<user name>",
    /* The provider the APPOINTMENT WAS BOOKED WITH (Nittin, 2026-09-12) — not
       whoever is signed in writing the note. `serviceProviderName` is computed
       by api-appointment-service from the provider's consultation info, so it
       rides along on the appointment row phr already holds; when that lookup
       failed the key is absent, and the placeholder stands. */
    providerName: ((appointment && appointment.serviceProviderName) || "").trim() ||
      "<Service provider name>",
    /* The copy names the moment the call was due, not the slot's range. */
    /* "--:-- AM/PM" is product's own placeholder, meridiem included — the copy
       reads "at --:-- AM/PM DD/MM/YY". */
    callTime: slotTime(slot) || "--:-- AM/PM",
    callDate: shortDate(appointment && appointment.appointmentDate) || "DD/MM/YY",
  };
};

/** Fill a template's tokens. An unknown token is left alone, not blanked. */
export const fillCallNoteTemplate = (message, context) =>
  String(message || "").replace(/\{(\w+)\}/g, (whole, key) =>
    Object.prototype.hasOwnProperty.call(context || {}, key) ? context[key] : whole
  );
